import { useEffect, useMemo, useState } from "react";
import { HitLoadingBlock } from "@/components/loading";
import { Link, useSearchParams } from "react-router-dom";
import { api, cardImageUrl, cardName } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { EditProfileCard } from "@/components/EditProfileCard";
import MyReputationCard from "@/components/MyReputationCard";
import { useDialog } from "@/lib/dialog";
import { toast } from "sonner";
import {
  ShoppingBag, Package, CheckCircle2, DollarSign, Truck, ShieldCheck,
  ClipboardList, History, MapPin, AlertTriangle, Clock, ArrowRight, Store, CreditCard, XCircle, Heart, Trash2, PackageX,
} from "lucide-react";
import { GameTabs } from "@/components/GameTabs";
import GamePreferencesCard from "@/components/GamePreferencesCard";
import { cardPath } from "@/lib/cardUrl";
import { RankProfileHeader, RankDetails, ChangeAvatarButton } from "@/components/avatar/RankPanel";

const STATUS_CLASS = {
  PENDIENTE_PAGO: "status-pill status-pendiente",
  PAGO_POR_VERIFICAR: "status-pill status-pendiente",
  PAGADO: "status-pill status-pagado",
  ENVIADO: "status-pill status-enviado",
  ENTREGADO: "status-pill status-entregado",
  PENDIENTE_LIBERACION: "status-pill status-entregado",
  LIBERADO: "status-pill status-liberado",
  EN_DISPUTA: "status-pill status-disputa",
  REEMBOLSADO: "status-pill status-disputa",
  RESUELTA_PARCIAL: "status-pill status-disputa",
  CANCELADO: "status-pill status-pendiente",
  RECHAZADO: "status-pill status-pendiente",
};

const STATUS_LABEL = {
  PENDIENTE_PAGO: "Pendiente de pago",
  PAGO_POR_VERIFICAR: "En revisión",
  PAGADO: "Pagado · Retenido",
  ENVIADO: "En camino",
  ENTREGADO: "Entregado (por confirmar)",
  PENDIENTE_LIBERACION: "Pendiente de liberación",
  LIBERADO: "Cerrado",
  EN_DISPUTA: "En disputa",
  REEMBOLSADO: "Reembolsado",
  RESUELTA_PARCIAL: "Resuelto parcial",
  CANCELADO: "Cancelado",
  RECHAZADO: "Rechazado",
};

// STRICT DELIMITATION:
//   Mis Compras (activas)   = PENDIENTE_PAGO, PAGADO     (acción del comprador: pagar, esperar despacho)
//   Seguimiento (en tránsito) = ENVIADO, ENTREGADO, EN_DISPUTA (acción del comprador: recibir, confirmar, disputa)
//   Historial (finalizados)  = LIBERADO, REEMBOLSADO, RESUELTA_PARCIAL, CANCELADO, RECHAZADO (solo lectura)
//   Perfil = identidad, verificación, provincia por defecto
const BUCKET = {
  active:  ["PENDIENTE_PAGO", "PAGO_POR_VERIFICAR", "PAGADO"],
  transit: ["ENVIADO", "ENTREGADO", "PENDIENTE_LIBERACION", "EN_DISPUTA"],
  history: ["LIBERADO", "REEMBOLSADO", "RESUELTA_PARCIAL", "CANCELADO", "RECHAZADO"],
};

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    if (["purchases", "tracking", "history", "wishlist", "profile"].includes(t)) return t;
    return "purchases";
  })();
  const [tab, setTab] = useState(initialTab);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  // Fase 2 · segmentación por juego para Mis Compras y Wishlist.
  const [ordersGame, setOrdersGame] = useState("all");
  const [wishlistGame, setWishlistGame] = useState("all");

  const loadOrders = () => api.get("/orders/mine", { params: { role: "buyer", ...(ordersGame !== "all" ? { game: ordersGame } : {}) } })
    .then(r => setOrders(r.data))
    .finally(() => setLoading(false));

  const loadWishlist = () => api.get("/wishlist", { params: wishlistGame !== "all" ? { game: wishlistGame } : {} }).then(r => setWishlist(r.data)).catch(() => setWishlist([]));

  useEffect(() => { loadOrders(); /* eslint-disable-next-line */ }, [ordersGame]);
  useEffect(() => { loadWishlist(); /* eslint-disable-next-line */ }, [wishlistGame]);

  const changeTab = (v) => { setTab(v); setSearchParams(v === "purchases" ? {} : { tab: v }); };

  const buckets = useMemo(() => ({
    active:  orders.filter(o => BUCKET.active.includes(o.status)),
    transit: orders.filter(o => BUCKET.transit.includes(o.status)),
    history: orders.filter(o => BUCKET.history.includes(o.status)),
  }), [orders]);

  const stats = {
    total: orders.length,
    held: orders.filter(o => ["PAGADO", "ENVIADO", "ENTREGADO", "PENDIENTE_LIBERACION"].includes(o.status)).reduce((a, o) => a + o.total, 0),
    completed: orders.filter(o => o.status === "LIBERADO").length,
    pendingPay: orders.filter(o => o.status === "PENDIENTE_PAGO").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-8 pb-24 md:pb-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl">PANEL DEL COMPRADOR</h1>
          <p className="text-sm text-neutral-500 mt-1">Mis Compras (activas) · Seguimiento (en tránsito) · Historial (finalizados) · Perfil (identidad).</p>
        </div>
        <div className="flex gap-2">
          <Link to="/catalog"><Button data-testid="buyer-browse-btn" className="bg-[#EF4444] hover:bg-[#DC2626]"><Store size={16} className="mr-2" />Ir al catálogo</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={ShoppingBag} label="Total pedidos" value={stats.total} />
        <Stat icon={Clock} label="Pendientes de pago" value={stats.pendingPay} highlight={stats.pendingPay > 0} />
        <Stat icon={DollarSign} label="Retenido" value={`$${stats.held.toFixed(2)}`} />
        <Stat icon={CheckCircle2} label="Compras cerradas" value={stats.completed} />
      </div>

      <Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList className="bg-[#111] border border-[#262626] flex-wrap h-auto">
          <TabsTrigger data-testid="buyer-tab-purchases" value="purchases" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <ClipboardList size={14} className="mr-1" />Mis Compras ({buckets.active.length})
          </TabsTrigger>
          <TabsTrigger data-testid="buyer-tab-tracking" value="tracking" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <Truck size={14} className="mr-1" />Seguimiento ({buckets.transit.length})
          </TabsTrigger>
          <TabsTrigger data-testid="buyer-tab-history" value="history" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <History size={14} className="mr-1" />Historial ({buckets.history.length})
          </TabsTrigger>
          <TabsTrigger data-testid="buyer-tab-wishlist" value="wishlist" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <Heart size={14} className="mr-1" />Deseos ({wishlist.length})
          </TabsTrigger>
          <TabsTrigger data-testid="buyer-tab-profile" value="profile" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <ShieldCheck size={14} className="mr-1" />Perfil / Verificación
          </TabsTrigger>
        </TabsList>

        {/* ==== MIS COMPRAS — pendientes de pago / pagadas en espera de despacho ==== */}
        <TabsContent value="purchases" className="mt-4">
          <GameTabs value={ordersGame} onChange={setOrdersGame} testidPrefix="buyer-orders" className="mb-4" />
          <SectionBanner>
            <b className="text-white">Mis Compras:</b> pedidos que requieren <b>tu acción</b> (pagar o esperar despacho). Cuando el vendedor envíe la carta, el pedido pasa a{" "}
            <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("tracking")}>Seguimiento</button>. Al cerrarse (liberado/reembolsado) aparecerá en{" "}
            <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("history")}>Historial</button>.
          </SectionBanner>
          {loading ? <Loading /> : buckets.active.length === 0 ? (
            <Empty>Sin compras activas. <Link to="/catalog" className="text-[#EF4444]">Explorar catálogo</Link></Empty>
          ) : (
            <OrderTable orders={buckets.active} kind="active" reload={loadOrders} />
          )}
        </TabsContent>

        {/* ==== SEGUIMIENTO — en tránsito / entregada por confirmar / disputa ==== */}
        <TabsContent value="tracking" className="mt-4">
          <SectionBanner>
            <b className="text-white">Seguimiento:</b> tus pedidos <b>en camino</b> o <b>por confirmar recepción</b>. Aquí ves la guía, confirmas entrega y abres disputa si algo va mal. La contabilidad y estado final vive en{" "}
            <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("history")}>Historial</button> una vez cerrado el pedido.
          </SectionBanner>
          {loading ? <Loading /> : buckets.transit.length === 0 ? (
            <Empty>No tienes pedidos en tránsito ahora mismo.</Empty>
          ) : (
            <OrderTable orders={buckets.transit} kind="transit" />
          )}
        </TabsContent>

        {/* ==== HISTORIAL — cerrados / reembolsados / cancelados ==== */}
        <TabsContent value="history" className="mt-4">
          <SectionBanner>
            <b className="text-white">Historial:</b> pedidos <b>finalizados</b> (cerrados, reembolsados o cancelados). Solo lectura — para acciones activas ve a{" "}
            <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("purchases")}>Mis Compras</button> o{" "}
            <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("tracking")}>Seguimiento</button>.
          </SectionBanner>
          {loading ? <Loading /> : buckets.history.length === 0 ? (
            <Empty>Todavía no tienes compras cerradas.</Empty>
          ) : (
            <OrderTable orders={buckets.history} kind="history" />
          )}
        </TabsContent>

        {/* ==== DESEOS · Wishlist ==== */}
        <TabsContent value="wishlist" className="mt-4">
          <GameTabs value={wishlistGame} onChange={setWishlistGame} testidPrefix="buyer-wishlist" className="mb-4" />
          <SectionBanner>
            <b className="text-white">Deseos:</b> guarda cartas que te interesan. Te avisamos cuando alguien las publique o baje el precio. Toca el corazón en cualquier carta del catálogo para agregarla.
          </SectionBanner>
          {wishlist.length === 0 ? (
            <Empty>Tu wishlist está vacía. <Link to="/catalog" className="text-[#EF4444]">Explora el catálogo</Link> y toca el corazón en las cartas que quieras seguir.</Empty>
          ) : (
            <WishlistGrid items={wishlist} reload={loadWishlist} />
          )}
        </TabsContent>

        {/* ==== PERFIL / VERIFICACIÓN ==== */}
        <TabsContent value="profile" className="mt-4 space-y-6">
          <BuyerProfileCard user={user} orders={orders} />
          <EditProfileCard />
          <GamePreferencesCard />
          <MyReputationCard />
          <ChangePasswordCard />
        </TabsContent>
      </Tabs>

      {/* Mobile floating action button — Seguir pedidos en tránsito */}
      {tab !== "tracking" && buckets.transit.length > 0 && (
        <button
          type="button"
          data-testid="buyer-fab-tracking"
          onClick={() => changeTab("tracking")}
          className="sm:hidden fixed bottom-6 right-4 z-40 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-2 font-semibold text-sm"
          aria-label={`Seguir ${buckets.transit.length} pedidos en tránsito`}
        >
          <Truck size={18} />
          Seguir pedidos
          <span className="bg-white text-[#EF4444] text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">{buckets.transit.length}</span>
        </button>
      )}
    </div>
  );
}

function WishlistGrid({ items, reload }) {
  const remove = async (id) => {
    try {
      await api.delete(`/wishlist/${id}`);
      toast.success("Quitado de tu wishlist");
      reload && reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="wishlist-grid">
      {items.map(w => {
        const to = cardPath({
          cardId: w.card_id,
          name: cardName(w.card, w.language),
          language: w.language,
          variant: w.variant,
          graded: w.graded_key === "raw" ? undefined : w.graded_key,
        });
        const img = w.card?.image_url;
        return (
          <div key={w.id} data-testid={`wishlist-item-${w.id}`} className={`bg-[#111] border rounded-md overflow-hidden ${w.graded_key !== "raw" ? "border-purple-500/50" : "border-[#262626]"}`}>
            <Link to={to} className="block card-hover">
              <div className="aspect-[3/4] bg-[#0A0A0A] flex items-center justify-center relative">
                {img ? (
                  <img src={img.startsWith("http") ? img : img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${img}`} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} />
                ) : (
                  <div className="text-neutral-600 text-xs">Sin imagen</div>
                )}
                <div className="absolute top-2 right-2 bg-[#EF4444]/90 text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase">{w.language}</div>
                {w.graded_key !== "raw" && (
                  <div className="absolute bottom-2 left-2 bg-purple-600/95 text-white text-[10px] px-2 py-0.5 rounded font-bold">🏆 {w.graded_key.replace("-", " ")}</div>
                )}
                {!w.in_stock && (
                  <div className="absolute bottom-2 right-2 bg-amber-500/90 text-white text-[10px] px-2 py-0.5 rounded font-bold inline-flex items-center gap-1"><PackageX size={10} /> Sin stock</div>
                )}
              </div>
              <div className="p-2">
                <div className="text-xs font-medium line-clamp-1">{cardName(w.card, w.language) || "—"}</div>
                <div className="text-[10px] text-neutral-500 line-clamp-1">{w.card?.set_name || "—"}</div>
                <div className="mt-1 flex items-baseline justify-between gap-1">
                  {w.min_price != null
                    ? <span className="font-display text-sm text-[#EF4444]">${Number(w.min_price).toFixed(2)}</span>
                    : <span className="text-[10px] text-neutral-500">Sin publicaciones</span>}
                  {w.seller_count > 0 && <span className="text-[9px] text-neutral-500">{w.seller_count} vend.</span>}
                </div>
              </div>
            </Link>
            <button
              type="button"
              onClick={() => remove(w.id)}
              data-testid={`wishlist-remove-${w.id}`}
              className="w-full py-1.5 text-[10px] text-red-400 hover:bg-red-500/10 border-t border-[#262626] flex items-center justify-center gap-1"
            >
              <Trash2 size={11} /> Quitar de wishlist
            </button>
          </div>
        );
      })}
    </div>
  );
}


// ============================================================
function SectionBanner({ children }) {
  return <div className="mb-3 text-xs text-neutral-500 p-2 rounded bg-[#0A0A0A] border border-[#262626]">{children}</div>;
}
function Loading() { return <HitLoadingBlock section="inventory" className="p-2" />; }
function Empty({ children }) { return <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500">{children}</div>; }

function OrderTable({ orders, kind, reload }) {
  const { confirm } = useDialog();
  const [busy, setBusy] = useState(null);

  const resume = async (o) => {
    setBusy(o.id + ":resume");
    try {
      const r = await api.post(`/orders/${o.id}/resume-payment`);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo retomar el pago");
      setBusy(null);
      reload && reload();
    }
  };

  const cancel = async (o) => {
    const ok = await confirm({
      title: "Cancelar pedido pendiente",
      description: "Se cancelará este pedido y las cartas quedarán disponibles nuevamente. ¿Continuar?",
      confirmLabel: "Sí, cancelar",
      danger: true,
    });
    if (!ok) return;
    setBusy(o.id + ":cancel");
    try {
      await api.post(`/orders/${o.id}/cancel-pending`);
      toast.success("Pedido cancelado. Las cartas vuelven al catálogo.");
      reload && reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {/* Desktop / tablet: table view */}
      <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto hidden sm:block">
      <table className="w-full text-sm">
        <thead className="text-neutral-500 text-xs uppercase tracking-wider border-b border-[#262626]">
          <tr>
            <th className="text-left p-3">Pedido</th>
            <th className="text-left p-3">Carta</th>
            <th className="text-left p-3">Estado</th>
            {kind === "transit" && <th className="text-left p-3">Guía / entrega</th>}
            {kind !== "transit" && <th className="text-right p-3">Total</th>}
            <th className="text-left p-3">Fecha</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id} data-testid={`buyer-order-${o.id}`} className="border-b border-[#171717] hover:bg-[#0F0F0F]">
              <td className="p-3 font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</td>
              <td className="p-3">
                <div className="flex items-center gap-2">
                  {(() => {
                    const first = (o.items && o.items[0]) || null;
                    const img = first?.card?.image_url || o.card?.image_url;
                    return img && <img src={cardImageUrl(img)} alt="" className="w-8 h-11 object-contain" onError={(e) => e.currentTarget.style.display = "none"} />;
                  })()}
                  <div>
                    <div className="text-white text-sm line-clamp-1">
                      {(o.items && o.items.length > 1)
                        ? `${o.item_count} cartas · ${cardName(o.items[0]?.card, o.items[0]?.publication_language) || ""}${o.items.length > 1 ? " +" + (o.items.length - 1) : ""}`
                        : (cardName(o.items?.[0]?.card, o.items?.[0]?.publication_language) || cardName(o.card, o.publication_language) || "—")}
                    </div>
                    <div className="text-neutral-500 text-xs">
                      {(o.items?.[0]?.publication_language || o.publication_language || "").toUpperCase()}
                      {o.items && o.items.length > 1 && " · varios ítems"}
                    </div>
                  </div>
                </div>
              </td>
              <td className="p-3"><span className={STATUS_CLASS[o.status]} data-testid={`buyer-status-${o.id}`}>{STATUS_LABEL[o.status]}</span>
                {o.status === "PAGO_POR_VERIFICAR" && <div className="text-[11px] text-amber-300 mt-1">Carta reservada. Espera a que verifiquemos tu pago.</div>}</td>
              {kind === "transit" && (
                <td className="p-3 text-xs text-neutral-400">
                  {o.delivery_method === "meetup" ? <span className="text-emerald-400">Encuentro presencial</span> :
                   o.guide_number ? <span className="text-white">Guía {o.guide_number}</span> :
                   <span className="text-amber-400">Sin guía aún</span>}
                </td>
              )}
              {kind !== "transit" && (
                <td className={`p-3 text-right font-display text-lg ${kind === "history" && ["REEMBOLSADO", "CANCELADO"].includes(o.status) ? "text-neutral-500 line-through" : "text-[#EF4444]"}`}>
                  ${o.total.toFixed(2)}
                </td>
              )}
              <td className="p-3 text-neutral-400 text-xs">{new Date(o.created_at).toLocaleDateString("es-EC")}</td>
              <td className="p-3">
                {kind === "active" && o.status === "PENDIENTE_PAGO" ? (
                  <div className="flex justify-end gap-2 flex-wrap">
                    <Button
                      size="sm"
                      data-testid={`buyer-resume-${o.id}`}
                      onClick={() => resume(o)}
                      disabled={busy === o.id + ":resume"}
                      className="bg-[#EF4444] hover:bg-[#DC2626] h-8 text-xs"
                    >
                      <CreditCard size={12} className="mr-1" />
                      {busy === o.id + ":resume" ? "Abriendo…" : "Continuar pago"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`buyer-cancel-${o.id}`}
                      onClick={() => cancel(o)}
                      disabled={busy === o.id + ":cancel"}
                      className="text-neutral-400 hover:text-[#EF4444] h-8 text-xs"
                    >
                      <XCircle size={12} className="mr-1" />Cancelar
                    </Button>
                  </div>
                ) : (
                  <Link
                    to={`/order/${o.id}`}
                    data-testid={kind === "active" ? `buyer-pay-${o.id}` : kind === "transit" ? `buyer-track-${o.id}` : `buyer-view-${o.id}`}
                    className="text-[#EF4444] text-sm hover:underline flex items-center gap-1 justify-end"
                  >
                    {kind === "active" ? "Ver detalle" :
                     kind === "transit" ? "Seguir / confirmar" :
                     "Ver detalle"}
                    <ArrowRight size={12} />
                  </Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Mobile: card view — no horizontal scroll, action button always visible */}
    <div className="sm:hidden space-y-3" data-testid="buyer-orders-cards">
      {orders.map(o => {
        const first = (o.items && o.items[0]) || null;
        const img = first?.card?.image_url || o.card?.image_url;
        const cardLabel = (o.items && o.items.length > 1)
          ? `${o.item_count} cartas · ${cardName(o.items[0]?.card, o.items[0]?.publication_language) || ""}${o.items.length > 1 ? " +" + (o.items.length - 1) : ""}`
          : (cardName(o.items?.[0]?.card, o.items?.[0]?.publication_language) || cardName(o.card, o.publication_language) || "—");
        const guideBlock = kind === "transit" ? (
          o.delivery_method === "meetup" ? <span className="text-emerald-400 text-xs">Encuentro presencial</span> :
          o.guide_number ? <span className="text-white text-xs">Guía {o.guide_number}</span> :
          <span className="text-amber-400 text-xs">Sin guía aún</span>
        ) : null;
        return (
          <div key={o.id} data-testid={`buyer-order-card-${o.id}`} className="bg-[#111] border border-[#262626] rounded-md p-3">
            <div className="flex items-start gap-3">
              {img && <img src={cardImageUrl(img)} alt="" className="w-12 h-16 object-contain shrink-0" onError={(e) => e.currentTarget.style.display = "none"} />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] text-neutral-500">#{o.id.slice(0, 8)}</span>
                  <span className="text-neutral-500 text-[10px]">{new Date(o.created_at).toLocaleDateString("es-EC")}</span>
                </div>
                <div className="text-white text-sm mt-1 line-clamp-2">{cardLabel}</div>
                <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                  <span className={STATUS_CLASS[o.status]} data-testid={`buyer-status-mobile-${o.id}`}>{STATUS_LABEL[o.status]}</span>
                  {o.status === "PAGO_POR_VERIFICAR" && <div className="text-[11px] text-amber-300 mt-1">Carta reservada. Espera a que verifiquemos tu pago.</div>}
                  {kind !== "transit" && (
                    <span className={`font-display text-base ${kind === "history" && ["REEMBOLSADO", "CANCELADO"].includes(o.status) ? "text-neutral-500 line-through" : "text-[#EF4444]"}`}>${o.total.toFixed(2)}</span>
                  )}
                </div>
                {guideBlock && <div className="mt-2">{guideBlock}</div>}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#262626]">
              {kind === "active" && o.status === "PENDIENTE_PAGO" ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    data-testid={`buyer-resume-mobile-${o.id}`}
                    onClick={() => resume(o)}
                    disabled={busy === o.id + ":resume"}
                    className="bg-[#EF4444] hover:bg-[#DC2626] flex-1 h-9 text-sm"
                  >
                    <CreditCard size={14} className="mr-1" />
                    {busy === o.id + ":resume" ? "Abriendo…" : "Continuar pago"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`buyer-cancel-mobile-${o.id}`}
                    onClick={() => cancel(o)}
                    disabled={busy === o.id + ":cancel"}
                    className="text-neutral-400 hover:text-[#EF4444] h-9 text-sm px-3"
                  >
                    <XCircle size={14} />
                  </Button>
                </div>
              ) : (
                <Link
                  to={`/order/${o.id}`}
                  data-testid={kind === "active" ? `buyer-pay-mobile-${o.id}` : kind === "transit" ? `buyer-track-mobile-${o.id}` : `buyer-view-mobile-${o.id}`}
                  className={`flex items-center justify-center gap-2 rounded-md h-10 text-sm font-medium ${kind === "transit" ? "bg-[#EF4444] hover:bg-[#DC2626] text-white" : "bg-[#171717] hover:bg-[#1f1f1f] text-[#EF4444] border border-[#262626]"}`}
                >
                  {kind === "transit" ? <><Truck size={14} />Seguir / confirmar</> :
                   kind === "active" ? <>Ver detalle<ArrowRight size={14} /></> :
                   <>Ver detalle<ArrowRight size={14} /></>}
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
    </>
  );
}

function BuyerProfileCard({ user, orders }) {
  const statusBadge = {
    aprobado: { label: "Verificado (vendedor)", cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
    pendiente_revision: { label: "Docs en revisión", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
    pendiente_documentos: { label: "Puedes comprar · Verifica para vender", cls: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
    rechazado: { label: "Rechazado", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  }[user?.verification_status] || { label: user?.verification_status || "—", cls: "bg-neutral-500/20 text-neutral-300 border-neutral-500/30" };

  const disputedCount = orders.filter(o => o.status === "EN_DISPUTA").length;

  return (
    <div className="space-y-6">
      <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="buyer-profile-card">
        <RankProfileHeader user={user} avatarTestid="buyer-profile-avatar" identity={<>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl break-words">{user?.name}</h2>
            <Badge className={statusBadge.cls}><ShieldCheck size={12} className="mr-1" />{statusBadge.label}</Badge>
          </div>
          <div className="text-sm text-neutral-400 mt-1 break-all">{user?.email} · {user?.phone || "sin teléfono"}</div>
          <div className="flex flex-wrap items-center gap-2">
            <ChangeAvatarButton testid="buyer-change-avatar" />
            {user?.verification_status !== "aprobado" && (
              <Link to="/verify?intent=publish" className="mt-2"><Button size="sm" variant="outline" data-testid="buyer-goto-verify" className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">Verificar para vender</Button></Link>
            )}
          </div>
        </>} />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[#262626]">
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Provincia por defecto</div>
            <div className="text-sm mt-1 flex items-center gap-1 text-neutral-200"><MapPin size={12} className="text-neutral-500" />{user?.province || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Ciudad</div>
            <div className="text-sm mt-1 text-neutral-200">{user?.city || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Cédula</div>
            <div className="font-mono text-sm mt-1 text-neutral-300">{user?.cedula || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Disputas activas</div>
            <div className={`font-display text-xl mt-1 ${disputedCount > 0 ? "text-red-400" : "text-neutral-400"}`}>{disputedCount}</div>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[#262626] text-xs text-neutral-500 leading-relaxed">
          <b className="text-white">Dirección de envío:</b> por seguridad, la dirección exacta se ingresa en cada compra al pasar por caja. Solo el vendedor de tu pedido puede verla, y solo tras el pago confirmado.
        </div>
      </div>

      <RankDetails />

      {disputedCount > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 flex items-start gap-3" data-testid="buyer-disputes-alert">
          <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <div className="font-semibold text-red-200">Tienes {disputedCount} disputa(s) activa(s)</div>
            <div className="text-sm text-neutral-300 mt-1">Revísalas en <button className="text-[#EF4444] hover:underline" onClick={() => document.querySelector('[data-testid="buyer-tab-tracking"]')?.click()}>Seguimiento</button> para responder al admin cuanto antes.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`bg-[#111] border rounded-md p-4 stat-bar ${highlight ? "border-[#EF4444]" : "border-[#262626]"}`}>
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider"><Icon size={14} /> {label}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}
